import { useParams } from "react-router-dom";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { pink } from "@mui/material/colors";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { StatusChip, Typography, useSnackbar } from "@initia-digital/component-library";
import { baseDefinedAndNotNullNarrowing, BasicLogger } from "@initia-digital/ts-utils";

import { IPlanStatus } from "@initia-digital/types/work-and-safety"
import { patchPlan } from "../api/mutate";
import { useIsAdmin } from "../hooks";
import { AuthLibMSALTokenAcquisitionError, useMSALPayload } from "@initia-digital/auth-client-utils";
import { useWorkAndSafetyStore } from "../store";

const logger = new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2, logFormat: 'BROWSER' });

export const PlanHeader = ({
    id, title, status
}: {
    id?: string, title: string, status: IPlanStatus
}) => {
    // --------------------------------
    const {
        updateEditPlanState
    } = useWorkAndSafetyStore();
    
    const params = useParams();
    const { planId } = params;
    const isCreate = !baseDefinedAndNotNullNarrowing(planId);

    const authDetail = useMSALPayload();

    const theme = useTheme();
    const isSmallerThanMd = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    const { queueSnackbar } = useSnackbar();

    const isAdmin = useIsAdmin(authDetail, true);

    // MUTATIONS
    const queryClient = useQueryClient();
    const patchPlanMutation = useMutation({
        mutationFn: (variables: { 
            planId: string, 
            patches: {
                op: "add" | "remove" | "replace" | "move" | "copy" | "test"
                path: string
                value?: unknown
                from?: string
            }[], 
            notify: boolean, 
            authDetail: Awaited<ReturnType<typeof useMSALPayload>>,
            successMessage?: string,
            errorMessage?: string
        }) => {
            return patchPlan({
                planId: variables.planId,
                notify: variables.notify,
                patches: variables.patches,
                authDetail: variables.authDetail
            });
        },
        onSuccess: (planUpdate, variables) => {
            if (variables.successMessage) {
                queueSnackbar(variables.successMessage, 'success', 'Success', 3000, '1rem');
            }
            queryClient.invalidateQueries({ queryKey: ['plan', variables.planId] });
            queryClient.invalidateQueries({ queryKey: ['job', planUpdate.jobId, 'plan'] });
            queryClient.invalidateQueries({ queryKey: ['previousMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['currentMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['nextMonthPlanList'] });
            
            updateEditPlanState({
                ...planUpdate
            });
        },
        onError: (error, variables) => {
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error patching plan', { error });
            }
            logger.error('Error patching plan');
            const errorMessage = variables.errorMessage || 'Plan could not be updated';
            queueSnackbar(errorMessage, 'error', 'Error updating plan', 3000, '1rem');
        }
    });

    return (
        <Box
            display="flex" flexDirection="column"
        >
            <Box
                display="flex" flexDirection="column" alignItems="flex-start"
            >
                <Box
                    display="flex" flexDirection="row" alignItems="center"
                    bgcolor={pink[100]}
                    borderRadius="0px 10px 10px 0px"
                    pl="5px" pr="8px"
                >
                <Typography
                    fontSize={"0.8rem"}
                    fontWeight={300}
                    color={"black"}
                >
                    {isCreate ? 'Save to create a plan' : 'Save to update this plan'}
                </Typography>
                </Box>
            </Box>
            <Box
                display="flex" flexDirection="row" alignItems="center"
            >
                <Box
                    display="flex" flexDirection="column" alignItems={isSmallerThanMd ? "center" : "flex-start"}
                    mt={isSmallerThanSm ? "10px" : "0px"} mr="10px"
                >
                    <Typography
                        fontSize={isSmallerThanSm ? "0.9rem" : "1.2rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        {baseDefinedAndNotNullNarrowing(title) && title.trim() !== '' ? title : 'Untitled work plan'}
                    </Typography>
                </Box>
                <StatusChip
                    type="plan"
                    id={baseDefinedAndNotNullNarrowing(id) ? id : ''}
                    status={status}
                    isAdmin={isAdmin}
                    handleStatusChange={isCreate || status === 'APPROVED' ? undefined : (planId, status, notify = false) => {
                        // ----------------------------------------
                        patchPlanMutation.mutateAsync({
                            authDetail,
                            planId,
                            patches: [
                                {
                                    "op": "replace",
                                    "path": "/status",
                                    "value": status
                                }
                            ],
                            notify
                        });
                    }}
                />
            </Box>
        </Box>
    )
}